/* eslint react/jsx-key: off */
import {
    Edit,
    SimpleForm,
    useRecordContext,
    useUpdate,
    useRefresh,
    useNotify,
    TopToolbar,
    useRedirect,
} from 'react-admin';
import {
    Box,
    Button,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { Loading } from 'react-admin';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
    EMPTY,
    PairPoint,
    formatAmps,
    formatSeconds,
    pickTrace,
    useChartTheme,
} from './plotting';
import { ChannelChart } from './ChannelChart';
import { PickingToggle } from './PickingToggle';
import { ChannelWorkflowNav } from './WorkflowNav';

const MyTopToolbar = () => {
    const redirect = useRedirect();
    const record = useRecordContext();

    if (!record || !record.id) {
        return null;
    };

    return (
        <TopToolbar>
            <Button
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => redirect('show', 'instruments', record.experiment_id)}
            >
                Experiment
            </Button>
            <Button
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => redirect('show', 'instrument_channels', record.id)}
            >
                Channel
            </Button>
        </TopToolbar>
    );
}

const ContinueToIntegration = () => {
    const record = useRecordContext();
    const redirect = useRedirect();

    if (!record || (record.baseline_values ?? []).length === 0) {
        return null;
    }
    return (
        <Button
            variant="contained"
            size="small"
            endIcon={<ArrowForwardIcon />}
            onClick={() => redirect(`/instrument_channels/${record.id}/integrate`)}
        >
            Continue to integration
        </Button>
    );
};

// The spline, the corrected signal and the integral checks are all recomputed
// by the API, so a save is what produces the next frame of the plot. Requests
// are queued rather than fired in parallel: two in flight can resolve out of
// order and the older response would put a stale baseline back on the chart.
// The record is updated from the response itself, so no refetch races the save.
const useBaselineSave = () => {
    const record = useRecordContext();
    const [update] = useUpdate();
    const notify = useNotify();
    const refresh = useRefresh();
    const queue = useRef<Promise<unknown>>(Promise.resolve());

    return useCallback((points: PairPoint[]) => {
        if (!record) {
            pickTrace('not saved: no record loaded yet');
            return queue.current;
        }
        pickTrace('queued for saving', { points: points.length });
        queue.current = queue.current
            .catch(() => undefined)
            .then(() => {
                pickTrace('sending to the API', { points: points.length });
                return update('instrument_channels', {
                    id: record.id,
                    data: { baseline_chosen_points: points },
                });
            })
            .then(() => {
                pickTrace('saved', { points: points.length });
                notify('Baseline updated', { autoHideDuration: 500 });
            })
            .catch((error) => {
                pickTrace('save failed, the plot is ahead of the database', {
                    error: String(error),
                });
                notify('Error: Changes could not be saved');
                console.error(error);
                // The plot is now ahead of the database
                refresh();
            });
        return queue.current;
    }, [update, record, notify, refresh]);
};

const LinePlotEdit = () => {
    const record = useRecordContext();
    const { setValue, watch, getValues } = useFormContext();
    const baselinePoints: PairPoint[] = watch('baseline_chosen_points') ?? EMPTY;
    const save = useBaselineSave();

    // Null until the record loads, which leaves picking off: a stray click
    // writes to the database.
    const [adding, setAdding] = useState<boolean | null>(null);
    useEffect(() => {
        if (record && adding === null) {
            setAdding((record.baseline_chosen_points ?? []).length === 0);
        }
    }, [record, adding]);

    if (!record) {
        return <Loading />;
    }
    if (!record.raw_values || !record.time_values) {
        return <Typography variant="h6">No data to display</Typography>;
    }

    // A point already on the baseline is identified by its position on the
    // curve, so clicking it again takes it back off. The list comes from the
    // form rather than the render closure, so two quick picks cannot both
    // build on the same starting point.
    const handlePick = (point: PairPoint) => {
        const current: PairPoint[] = getValues('baseline_chosen_points') ?? EMPTY;
        const existing = current.findIndex(chosen => chosen.x === point.x);
        const updatedPoints = existing === -1
            ? [...current, point].sort((a, b) => a.x - b.x)
            : current.filter((_, index) => index !== existing);

        setValue('baseline_chosen_points', updatedPoints);
        save(updatedPoints);
    };

    return (
        <Box sx={{ width: '100%', minWidth: 0 }}>
            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={1.5}
                sx={{ mb: -1 }}
            >
                {adding === true && (
                    <Typography variant="caption" color="text.secondary">
                        Click anywhere to add the point in the tooltip, hover a point and click it to remove
                    </Typography>
                )}
                <PickingToggle
                    adding={adding === true}
                    onToggle={() => setAdding(adding !== true)}
                    addLabel="Edit points"
                    stopLabel="Stop editing"
                    icon={<EditIcon />}
                />
            </Stack>
            <ChannelChart
                title={record.channel_name}
                time={record.time_values}
                raw={record.raw_values}
                spline={record.baseline_spline}
                corrected={record.baseline_values}
                picked={baselinePoints}
                pickedLabel="Selected points"
                pickFrom="raw"
                pickingEnabled={adding === true}
                onPick={handlePick}
            />
        </Box>
    );
};

const BaselinePointsTable = () => {
    const { watch, getValues, setValue } = useFormContext();
    const { colors } = useChartTheme();
    const save = useBaselineSave();
    const points: PairPoint[] = watch('baseline_chosen_points') ?? EMPTY;

    const removePoint = (index: number) => {
        const updatedPoints = [...(getValues('baseline_chosen_points') ?? [])];
        updatedPoints.splice(index, 1);
        setValue('baseline_chosen_points', updatedPoints, { shouldValidate: true, shouldDirty: true });
        save(updatedPoints);
    };

    return (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, width: '100%' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Baseline points</Typography>
            {points.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No points yet. Click a point on the raw signal.
                </Typography>
            ) : (
                <Table
                    size="small"
                    sx={{
                        '& .MuiTableCell-root': { py: 0.25 },
                        '& tbody tr:hover': { bgcolor: 'action.hover' },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" />
                            <TableCell>Time [s]</TableCell>
                            <TableCell>Current [A]</TableCell>
                            <TableCell padding="checkbox" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {points.map((point, index) => (
                            <TableRow key={`${point.x}-${index}`}>
                                    <TableCell padding="checkbox">
                                        {/* Same colour as the markers on the plot above */}
                                        <Box sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            bgcolor: colors.selected,
                                        }} />
                                    </TableCell>
                                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                        {formatSeconds(point.x)}
                                    </TableCell>
                                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                        {formatAmps(point.y)}
                                    </TableCell>
                                    <TableCell padding="checkbox">
                                        <IconButton
                                            size="small"
                                            aria-label="Remove point"
                                            onClick={() => removePoint(index)}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Paper>
    );
};

const InstrumentChannelEdit = () => (
    <Edit actions={<MyTopToolbar />}>
        <SimpleForm toolbar={false}>
            <ChannelWorkflowNav
                step={1}
                hint="Choose points along the flat regions of the raw signal; the spline updates after each point. Turn on Edit points to change the selection."
                action={<ContinueToIntegration />}
            />
            <LinePlotEdit />
            <BaselinePointsTable />
        </SimpleForm>
    </Edit>
);

export default InstrumentChannelEdit;
